import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import Card from '../ui/Card';
import type { SettingsHubPanelProps } from '../../modules/configuration-settings/models';

const SettingsHubPanel: React.FC<SettingsHubPanelProps> = ({
  title,
  badge,
  description,
  helpItems,
  headerActions,
  children,
  footer,
  className,
  contentClassName,
}) => {
  const { colors } = useTheme();

  return (
    <Card variant="outlined" className={cn('overflow-hidden p-0', className)}>
      <div
        className={cn(
          'relative border-b px-4 py-5 md:px-6 md:py-6',
          colors.border,
          'bg-gradient-to-br from-indigo-500/[0.08] via-violet-500/[0.05] to-transparent dark:from-indigo-500/10 dark:via-violet-500/[0.06]',
        )}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn('text-lg font-semibold tracking-tight', colors.text)}>{title}</h2>
              {badge}
            </div>
            {description ? (
              <div className={cn('max-w-3xl text-sm leading-relaxed', colors.textMuted)}>{description}</div>
            ) : null}
            {helpItems?.map((item, i) => (
              <details key={i} className="group max-w-3xl pt-1">
                <summary
                  className={cn(
                    'cursor-pointer list-none text-xs font-medium outline-none',
                    colors.textMuted,
                    'marker:content-none [&::-webkit-details-marker]:hidden',
                  )}
                >
                  <span className="underline decoration-dotted underline-offset-2 group-open:text-indigo-600 dark:group-open:text-indigo-400">
                    {item.summary}
                  </span>
                </summary>
                <div className={cn('mt-2 text-xs leading-relaxed', colors.textMuted)}>{item.content}</div>
              </details>
            ))}
          </div>
          {headerActions ? (
            <div className="flex shrink-0 items-center gap-1 self-start">{headerActions}</div>
          ) : null}
        </div>
      </div>
      <div className={cn('p-4 md:p-6', contentClassName)}>{children}</div>
      {footer ? <div className={cn('border-t px-4 py-4 md:px-6 md:py-4', colors.border)}>{footer}</div> : null}
    </Card>
  );
};

export default SettingsHubPanel;
